import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bonus,
  DepositMethodsResponse,
  makeFloat,
  useGetBonusesTransactions,
  useGetFullInfo,
  useMakeDeposit,
  useMakeRedirectDeposit
} from 'react-easyrocket';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useGetBalance } from 'src/hooks/useGetBalance';
import { useLocalCurrency } from 'src/hooks/useLocalCurrency';
import { useModalContext } from 'src/hooks/useModalContext';
import { useAmountPresets } from 'src/hooks/walletDepositHooks/useAmountPreset';
import { usePromocode } from '../../hooks/usePromocode';
import { useSavePathFromDeposit } from 'src/modules/depositReturnPath/hooks';
import { FieldType, useForm, validateMaxLength, validateNumbersString } from 'src/hooks/useForm';
import { DEPOSIT_PREV_PATH_LS_KEY } from 'src/modules/depositReturnPath/config';
import { additionalButtonContent } from 'src/pages/AccountDeposit/utils/additionalButtonContent';
import { Dropdown } from 'src/components/Dropdown';
import { prepareVoucherOptions, validateDepositButton } from 'src/utils';
import { Skeletons } from 'src/components/Skeletons';
import { AmountPresets } from '../AmountPresets';
import { Input } from 'src/components/Inputs/Input';
import { Button } from 'src/components/Buttons/Button';
import classNames from 'classnames';
import { DepositBonuses } from '../DepositBonuses';
import { DepositPromoCode } from '../DepositPromoCode';
import { PaymentMethod } from 'src/types';
import { setDepositInProgress } from 'src/utils/transactionTypeFromLS';
import { validateTransactionValue } from 'src/pages/AccountDeposit/utils/validators';

export const FiatBlock = ({
  isLoadingDepositMethods,
  isSuccess,
  depositMethods,
  paymentMethod,
  isModal
}: {
  isLoadingDepositMethods: boolean;
  isSuccess: boolean;
  depositMethods: DepositMethodsResponse;
  paymentMethod?: PaymentMethod;
  isModal: boolean;
}) => {
  const promoCodeRef = useRef<HTMLInputElement>(null);
  const { currencyName } = useLocalCurrency();
  const { shortBalance, currencyIcon } = useGetBalance();
  const { refetch: refetchBonusesTransactions } = useGetBonusesTransactions(
    {
      // eslint-disable-next-line camelcase
      bonus_type: 'All',
      status: ['Pending']
    },
    { enabled: false, isNeedPages: false }
  );

  const { t } = useTranslation();
  const location = useLocation();
  const { openModal } = useModalContext();
  const { mutateAsync, isLoading } = useMakeDeposit();
  const { fullInfo: { email: userEmail } = {} } = useGetFullInfo({});
  const makeRedirectDeposit = useMakeRedirectDeposit();
  const [showDepositBonuses, setShowDepositBonuses] = useState(false);

  const amountPresets = useAmountPresets({ minPayment: depositMethods?.min });
  const [selectedBonus, setSelectedBonus] = useState<Bonus | null>(null);
  const hasBonusAmountError = useRef<boolean>(false);
  const { promoCode, handleChangePromoCode, isPromocodeError, isLoadingCheckPromocode } =
    usePromocode({
      selectedBonus,
      setSelectedBonus,
      setShowDepositBonuses,
      isLoadingDepositMethods
    });
  const { savePath } = useSavePathFromDeposit();

  const {
    values,
    errors,
    fieldsProps: { amountProps },
    setValues,
    isFormValid,
    updateDefaultStatePartially
  } = useForm<{ amount: FieldType.DecimalNumber }>({
    amount: {
      placeholder: `${t('general.minDeposit')}:  ${currencyIcon}${makeFloat(depositMethods?.min)}`,
      fieldType: FieldType.DecimalNumber,
      validation: [
        validateMaxLength(20),
        validateNumbersString,
        validateTransactionValue({
          globalMax: depositMethods?.max,
          globalMin: depositMethods?.min,
          currencyIcon,
          currencyName,
          selectedPaymentMethod: paymentMethod,
          selectedBonus,
          hasBonusAmountError
        })
      ],
      defaultValue: '100'
    }
  });

  useEffect(() => {
    updateDefaultStatePartially({ amount: amountPresets[1] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountPresets]);

  const changeDepositSumm = (value: string) => {
    setValues({ amount: value });
  };

  const handleVoucherDropdownChange = (element: string | undefined) => {
    setValues({ amount: element || '0.00' });
  };

  const handleSubmitClick = useCallback(async () => {
    handleSetCurrentPageInLocalStorage();
    const method = paymentMethod?.method;
    const objectToSend = {
      method: method!,
      amount: parseInt(values.amount!, 10) * 100,
      promocode: hasBonusAmountError.current ? '' : promoCode,
      email: userEmail
    };

    const response = await mutateAsync(objectToSend);

    refetchBonusesTransactions();
    if (!response) {
      return;
    }
    savePath();
    if (response?.data?.method?.toUpperCase() === 'POST' && response?.data?.formFields) {
      setDepositInProgress();
      openModal('FORM_FIELDS', {
        replace: true,
        props: {
          ...response.data,
          paymentMethodName: paymentMethod?.title,
          replace: true
        }
      });
    } else if (response?.data?.method === 'POST' && response?.data?.params) {
      makeRedirectDeposit.mutate({
        params: response.data.params,
        url: response.data.checkoutUrl
      });
    } else if (response?.data?.checkoutUrl) {
      setTimeout(() => {
        window.open(response.data.checkoutUrl, '_self');
      });
    }
  }, [
    paymentMethod?.method,
    paymentMethod?.type,
    paymentMethod?.title,
    values.amount,
    promoCode,
    userEmail,
    mutateAsync,
    refetchBonusesTransactions,
    savePath,
    openModal,
    makeRedirectDeposit
  ]);

  const handleSelectBonus = (bonus: Bonus | null) => {
    setSelectedBonus(bonus);
    if (bonus?.promocode) {
      handleChangePromoCode(bonus.promocode);
    } else {
      handleChangePromoCode('');
    }

    if (
      bonus &&
      getMinDepositOfBonus({ bonus, currency: currencyName }) > Number(amountProps.value)
    ) {
      setValues({
        amount: `${getMinDepositOfBonus({ bonus, currency: currencyName })}`
      });
      return;
    }
  };

  /** При вызове депозита и переадоресации пользователя на сервис пополнения,
   * сохраняем текущий url страницы что бы потом вернуть на неё пользователя  */
  const handleSetCurrentPageInLocalStorage = () => {
    sessionStorage.setItem(DEPOSIT_PREV_PATH_LS_KEY, location.pathname);
  };

  const buttonContent = additionalButtonContent({
    selectedBonus,
    amount: values.amount,
    currencyIcon: currencyIcon,
    currencyName,
    hasError: !isFormValid
  });

  return (
    <>
      <div ref={promoCodeRef}></div>
      {paymentMethod?.type === 'voucher' && paymentMethod?.options && (
        <div className="z-[21]">
          <Dropdown
            value={values.amount}
            options={prepareVoucherOptions(paymentMethod?.options)}
            onChange={handleVoucherDropdownChange}
            placeholder={`${t('accountWalletDeposite.selectAmount')}`}
          />
        </div>
      )}
      {isLoadingDepositMethods ? (
        <div className="flex flex-row gap-2 justify-center w-full">
          <Skeletons count={4} className="h-10 flex-1 rounded-[10px]" />
        </div>
      ) : (paymentMethod?.type === 'voucher' && !paymentMethod?.options) ||
        paymentMethod?.type !== 'voucher' ? (
        <AmountPresets
          amountPresets={amountPresets}
          onClick={changeDepositSumm}
          value={values.amount}
          disableAll={!isSuccess}
        />
      ) : null}
      {isLoadingDepositMethods ? (
        <Skeletons className="w-full h-10" />
      ) : paymentMethod?.type !== 'voucher' ||
        (paymentMethod?.type === 'voucher' && !paymentMethod.options?.length) ? (
        <>
          <Input
            autoComplete="off"
            {...amountProps}
            onChange={(e) => {
              amountProps.onChange(e);
              changeDepositSumm(e.target.value);
            }}
            className="text-[16px] font-bold pl-[10px] placeholder:text-center text-center"
            disabled={!isSuccess}
            rightIcon={
              <div className="text-[16px] text-wdap_amount_icon font-bold">{currencyIcon}</div>
            }
          />
          {errors.amount && <span className="text-wdap_error text-xs">{t(errors.amount)}</span>}
        </>
      ) : null}

      <div className="mb-3 mt-[15px] flex justify-center w-full">
        <Button
          title={`${t('accountWalletBalance.deposite')}`}
          disabled={validateDepositButton(isFormValid, paymentMethod, hasBonusAmountError.current)}
          mode="button-primary"
          className={classNames('w-full max-w-full flex', isLoading ? 'flex-row' : 'flex-col')}
          onClick={handleSubmitClick}
          loading={isLoading}>
          <span
            className={classNames(
              buttonContent
                ? 'font-bold uppercase pr-[5px]'
                : 'text-[15px] font-bold text-center uppercase'
            )}>
            {buttonContent
              ? `${t('accountWalletBalance.deposite')} & ${t('general.play')}`
              : t('accountWalletBalance.deposite')}
          </span>
          {buttonContent && (
            <span className="text-[15px] text-center font-normal">{buttonContent}</span>
          )}
        </Button>
      </div>
      <DepositBonuses
        currencyIcon={currencyIcon}
        shortBalance={shortBalance}
        handleSelectBonus={handleSelectBonus}
        selectedBonus={selectedBonus}
        isModal={isModal}
        showDepositBonuses={showDepositBonuses}
        setShowDepositBonuses={setShowDepositBonuses}
        isLoadingDepositMethods={isLoadingDepositMethods}
      />
      <DepositPromoCode
        value={promoCode}
        changePromoCode={handleChangePromoCode}
        placeholder={`${t('general.placeholderPromo')}`}
        isModal={isModal}
        isPromocodeError={isPromocodeError}
        isLoadingCheckPromocode={isLoadingCheckPromocode}
      />
    </>
  );
};

type GetMinDepositOfBonusProps = { bonus: Bonus | null; currency: string };

function getMinDepositOfBonus({ currency, bonus }: GetMinDepositOfBonusProps) {
  return parseFloat(makeFloat(bonus?.minDepositAmount[currency]));
}
